import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { Section, Champ } from '../components/LegalPage';
import { EDITEUR } from '../legalInfo';

export default function CGU() {
  return (
    <LegalPage
      titre="Conditions générales d'utilisation"
      chapo="Les présentes CGU régissent l'accès et l'utilisation de la plateforme Jalonne. Les conditions financières de l'abonnement font l'objet de conditions générales de vente distinctes."
    >
      <Section titre="1. Objet et acceptation">
        <p>
          Jalonne est un service en ligne (SaaS) permettant aux entreprises du bâtiment de suivre
          l'avancement de leurs chantiers et de le partager avec leurs clients au moyen d'une page
          publique accessible par QR code.
        </p>
        <p>
          La création d'un compte emporte acceptation pleine et sans réserve des présentes CGU et
          des <Link to="/cgv">CGV</Link>. L'utilisateur qui n'accepte pas ces conditions doit
          renoncer à utiliser le service.
        </p>
      </Section>

      <Section titre="2. Définitions">
        <dl className="legal-dl">
          <dt>Éditeur</dt>
          <dd>L'entreprise exploitant Jalonne, identifiée dans les <Link to="/mentions-legales">mentions légales</Link>.</dd>
          <dt>Client</dt>
          <dd>L'entreprise professionnelle titulaire d'un abonnement, disposant d'un espace dédié (« tenant »).</dd>
          <dt>Utilisateur</dt>
          <dd>Toute personne physique disposant d'identifiants sur l'espace d'un Client (rôle administrateur ou membre).</dd>
          <dt>Visiteur</dt>
          <dd>Toute personne consultant une page publique de chantier, sans compte ni authentification.</dd>
          <dt>Contenu Client</dt>
          <dd>L'ensemble des données publiées par un Client : chantiers, photos, descriptions, intervenants, avancement.</dd>
        </dl>
      </Section>

      <Section titre="3. Description du service">
        <p>Selon la formule souscrite, la plateforme met à disposition :</p>
        <ul>
          <li>la création de chantiers avec avancement, photos, dates et intervenants ;</li>
          <li>une page publique par chantier et son QR code, destinée à être affichée sur site ;</li>
          <li>la personnalisation de l'espace public (logo, couleurs, slogan, nom de domaine) ;</li>
          <li>le suivi par email des chantiers pour les personnes qui s'y abonnent ;</li>
          <li>des rapports PDF et des statistiques de consultation.</li>
        </ul>
        <p>
          Les fonctionnalités incluses sont fixées par le contrat signé avec l'Éditeur. L'Éditeur
          peut faire évoluer les fonctionnalités ; les évolutions qui réduiraient substantiellement
          le périmètre souscrit sont traitées selon l'article 14 des <Link to="/cgv">CGV</Link>.
        </p>
      </Section>

      <Section titre="4. Compte et sécurité">
        <p>
          L'accès est réservé aux professionnels. Chaque Utilisateur dispose d'identifiants
          personnels, dont il assure la confidentialité. Toute action effectuée depuis un compte est
          réputée émaner de son titulaire.
        </p>
        <ul>
          <li>Le mot de passe comporte au minimum 8 caractères et est stocké sous forme de condensat (hachage), jamais en clair.</li>
          <li>L'authentification à deux facteurs (2FA) est disponible et peut être rendue obligatoire par l'administrateur du Client.</li>
          <li>L'adresse email doit être vérifiée pour activer le compte.</li>
          <li>Les tentatives de connexion sont journalisées à des fins de sécurité (voir la <Link to="/confidentialite">politique de confidentialité</Link>).</li>
        </ul>
        <p>
          L'Utilisateur informe sans délai l'Éditeur de toute utilisation non autorisée de son
          compte, à l'adresse <Champ valeur={EDITEUR.email} attendu="email de contact" />.
        </p>
      </Section>

      <Section titre="5. Engagements du Client sur ses contenus">
        <p>
          Le Client est seul responsable des contenus qu'il publie, y compris lorsqu'ils sont rendus
          accessibles au public par QR code. Il garantit notamment :
        </p>
        <ul>
          <li>
            <strong>disposer des autorisations nécessaires</strong> pour diffuser les photographies
            du chantier, en particulier l'accord du maître d'ouvrage lorsqu'il est requis par le
            marché ou le contrat de travaux ;
          </li>
          <li>
            <strong>respecter le droit à l'image des personnes</strong> : aucune photographie
            permettant d'identifier un compagnon, un riverain ou un tiers ne doit être publiée sans
            son accord ;
          </li>
          <li>
            <strong>ne publier aucune donnée sensible</strong> au sens de l'article 9 du RGPD, ni
            aucune donnée personnelle non nécessaire au suivi du chantier ;
          </li>
          <li>ne pas porter atteinte aux droits de tiers (marques, droits d'auteur, vie privée) ;</li>
          <li>ne diffuser aucun contenu illicite, diffamatoire, injurieux ou contraire à l'ordre public.</li>
        </ul>
        <p>
          Le Client fait son affaire de l'information des personnes concernées par les données qu'il
          saisit, conformément à l'<Link to="/sous-traitance">accord de sous-traitance</Link>.
        </p>
      </Section>

      <Section titre="6. Usages interdits">
        <p>Il est notamment interdit :</p>
        <ul>
          <li>de tenter d'accéder aux données d'un autre espace client ou d'en contourner le cloisonnement ;</li>
          <li>d'effectuer des tests d'intrusion, d'injection ou de déni de service sans autorisation écrite préalable ;</li>
          <li>d'extraire massivement le contenu de la plateforme par des moyens automatisés ;</li>
          <li>de revendre, sous-licencier ou mettre le service à disposition de tiers en dehors du périmètre souscrit ;</li>
          <li>d'utiliser le service pour émettre des messages non sollicités.</li>
        </ul>
        <p>
          La découverte d'une faille de sécurité doit être signalée de bonne foi à{' '}
          <Champ valeur={EDITEUR.email} attendu="email de contact" /> avant toute divulgation.
        </p>
      </Section>

      <Section titre="7. Disponibilité et maintenance">
        <p>
          L'Éditeur est tenu d'une obligation de moyens quant à la disponibilité du service. Il peut
          interrompre l'accès pour maintenance, en privilégiant les plages horaires de moindre
          activité et en informant les Clients des interruptions programmées significatives.
        </p>
        <p>
          Aucune garantie de disponibilité chiffrée (SLA) n'est accordée en dehors d'un engagement
          écrit spécifique. L'Éditeur n'est pas responsable des indisponibilités imputables au
          réseau internet, au terminal de l'Utilisateur ou à un fournisseur tiers.
        </p>
      </Section>

      <Section titre="8. Propriété intellectuelle et licence">
        <p>
          L'Éditeur concède au Client, pour la durée de l'abonnement, un droit d'utilisation
          personnel, non exclusif et non cessible de la plateforme, limité à ses besoins propres.
          Aucun transfert de propriété n'intervient.
        </p>
        <p>
          Le Client reste propriétaire de ses contenus. Il concède à l'Éditeur, pour la seule durée
          nécessaire à l'exécution du service, les droits d'héberger, reproduire, redimensionner et
          afficher ces contenus aux fins de les restituer sur les pages publiques, les exports et
          les rapports.
        </p>
      </Section>

      <Section titre="9. Suspension et résiliation pour manquement">
        <p>
          En cas de manquement grave aux présentes CGU, l'Éditeur peut suspendre l'accès après mise
          en demeure restée sans effet pendant quinze (15) jours, ou sans préavis en cas d'atteinte
          à la sécurité de la plateforme, de contenu manifestement illicite ou de risque pour des
          tiers.
        </p>
        <p>
          La résiliation de l'abonnement et le sort des données sont régis par les{' '}
          <Link to="/cgv">CGV</Link>.
        </p>
      </Section>

      <Section titre="10. Responsabilité">
        <p>
          L'Éditeur ne répond pas des contenus publiés par les Clients, ni de l'usage qu'ils font du
          service, ni des décisions prises sur la base des informations affichées. Les limitations
          de responsabilité applicables figurent à l'article 9 des <Link to="/cgv">CGV</Link>.
        </p>
        <p>
          Le Client garantit l'Éditeur contre toute réclamation de tiers résultant des contenus
          qu'il a publiés.
        </p>
      </Section>

      <Section titre="11. Modification des CGU">
        <p>
          L'Éditeur peut modifier les présentes CGU. Les Clients sont informés par email au moins
          trente (30) jours avant l'entrée en vigueur des modifications substantielles. La poursuite
          de l'utilisation après cette date vaut acceptation ; à défaut, le Client peut résilier son
          abonnement sans frais avant l'échéance.
        </p>
      </Section>

      <Section titre="12. Droit applicable et juridiction">
        <p>
          Les présentes CGU sont soumises au droit français. À défaut de résolution amiable, tout
          litige relève des tribunaux compétents du ressort de l'établissement principal de l'Éditeur, le Client
          agissant en qualité de professionnel.
        </p>
      </Section>
    </LegalPage>
  );
}
